import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;
const money = (v) => `$${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const day = (v) => (v ? new Date(v).toLocaleDateString() : '');

const Row = ({ label, value, strong, testId }) => (
  <div className={`flex justify-between text-sm ${strong ? 'font-semibold' : ''}`} data-testid={testId}><span className="text-gray-600">{label}</span><span>{value}</span></div>
);

const Details = ({ inv }) => (
  <div className="mt-4 border-t pt-4 space-y-3" data-testid={`portal-invoice-details-${inv.id}`}>
    {(inv.items || []).length > 0 && (
      <div className="space-y-1">
        {inv.items.map((it, i) => (
          <div key={i} className="flex justify-between text-sm"><span>{it.quantity || 1} × {it.name || it.product_name || it.description || 'Item'}</span><span>{money(it.total ?? (it.unit_price || it.price || 0) * (it.quantity || 1))}</span></div>
        ))}
      </div>
    )}
    <div className="space-y-1 border-t pt-2">
      <Row label="Subtotal" value={money(inv.subtotal)} />
      {Number(inv.tax || 0) > 0 && <Row label={`Sales tax${inv.tax_details?.rate ? ` (${(inv.tax_details.rate * (inv.tax_details.rate < 1 ? 100 : 1)).toFixed(3)}%)` : ''}`} value={money(inv.tax)} />}
      <Row label="Total" value={money(inv.total)} strong testId={`portal-invoice-total-${inv.id}`} />
      {Number(inv.refunded_amount || 0) > 0 ? (
        <>
          <Row label="Amount paid" value={money(inv.gross_paid ?? Number(inv.amount_paid || 0) + Number(inv.refunded_amount))} testId={`portal-invoice-gross-${inv.id}`} />
          <Row label="Refunded" value={`-${money(inv.refunded_amount)}`} testId={`portal-invoice-refunded-${inv.id}`} />
          <Row label="Remaining paid" value={money(inv.amount_paid)} testId={`portal-invoice-net-${inv.id}`} />
        </>
      ) : <Row label="Paid" value={money(inv.amount_paid)} />}
      <Row label="Balance due" value={money(inv.balance_due)} strong testId={`portal-invoice-balance-${inv.id}`} />
    </div>
    {(inv.payments || []).map((p) => <Row key={p.id || p.reference_number} label={`Payment ${day(p.created_at)}`} value={money(p.amount)} />)}
    {(inv.refunds || []).map((r, i) => <Row key={r.id || i} label={`Refund ${day(r.at)}`} value={`-${money(r.amount)}`} testId={`portal-invoice-refund-${inv.id}-${i}`} />)}
  </div>
);

export const PortalInvoiceCard = ({ inv, focused, headers, onPay }) => {
  const [open, setOpen] = useState(focused);
  const ref = useRef(null);
  useEffect(() => { if (focused) ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, [focused]);
  const download = async () => {
    try {
      const r = await axios.get(`${API}/api/customer-portal/invoices/${inv.id}/pdf`, { headers, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = `Invoice_${inv.invoice_number || inv.id.slice(0, 8)}.pdf`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error('Could not download the PDF'); }
  };
  const canPay = Number(inv.balance_due ?? inv.total) > 0.004 && !['paid', 'cancelled', 'draft', 'refunded'].includes(inv.status);
  const label = inv.payment_status === 'refunded' ? 'Refunded' : inv.payment_status === 'partially_refunded' ? 'Partially refunded' : inv.status || 'Pending';
  const tone = ['refunded', 'partially_refunded'].includes(inv.payment_status) ? 'bg-gray-100 text-gray-700' : inv.status === 'paid' ? 'bg-green-100 text-green-700' : inv.status === 'overdue' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700';
  return (
    <div ref={ref} className={`border rounded-lg p-4 ${focused ? 'ring-2 ring-amber-500 bg-amber-50/40' : 'hover:bg-gray-50'}`} data-testid={focused ? 'portal-focused-invoice' : `portal-invoice-${inv.id}`}>
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold">Invoice #{inv.invoice_number || inv.id.slice(0, 8)}</h3>
          <p className="text-sm text-gray-600">{day(inv.created_at)}</p>
        </div>
        <div className="text-right">
          <p className="text-lg font-semibold">{money(inv.total)}</p>
          <Badge className={tone} data-testid={`portal-invoice-status-${inv.id}`}>{label}</Badge>
        </div>
      </div>
      {open && <Details inv={inv} />}
      <div className="mt-3 flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={() => setOpen(!open)} data-testid={`portal-invoice-toggle-${inv.id}`}>{open ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />}{open ? 'Hide details' : 'View invoice'}</Button>
        <Button size="sm" variant="outline" onClick={download} data-testid={`portal-invoice-pdf-${inv.id}`}><Download className="w-4 h-4 mr-1" />Download PDF</Button>
        {canPay && <Button size="sm" className="bg-amber-600 hover:bg-amber-700" onClick={() => onPay(inv.id)} data-testid={`portal-invoice-pay-${inv.id}`}>Pay Now</Button>}
      </div>
    </div>
  );
};
